import { Paper, styled } from '@mui/material';

export const ChatContainer = styled(Paper)<{
  isOpen?: boolean;
  isActive?: boolean;
  isCurrentUser?: boolean;
  showChat?: boolean;
  openModal?: boolean;
  optVariant?:
  'main' | 'inner' | 'body' | 'tabs' | 'chatsHeader' | 'chatsInner' | 'chatsItem'
  | 'chatsItemInner' | 'tabContent' | 'tabContentHeader' | 'messages' | 'messageBubble'
  | 'messageContent' | 'input' | 'inputButton' | 'chatWidget' | 'emptyChatState' | 'emptyChatStateIcon'
  | 'chatModalHeader' | 'chatModalHeaderIcon'
}>(
  ({ theme, isOpen, isActive, isCurrentUser, showChat, openModal, optVariant = 'main' }) => {
    switch (optVariant) {
      case 'inner':
        return {
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'none',
          border: '1px solid #E0E0E0',
          height: '100%',
          minHeight: '100%',
        };

      case 'body':
        return {
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
          boxShadow: 'none',
          flexDirection: 'column',
          [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
          },
        };

      case 'main':
      default:
        return {
          width: '100%',
          height: '100vh',
          [theme.breakpoints.up('md')]: {
            width: '80%',
          },
          [theme.breakpoints.up('lg')]: {
            width: '70%',
          },
          display: 'flex',
          flexDirection: 'column',
          padding: theme.spacing(2),
          backgroundColor: 'white',
          position: 'fixed',
          right: 0,
          top: 0,
          zIndex: 1100,
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          visibility: 'hidden',
          opacity: 0,
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          maxWidth: '100vw',
          boxSizing: 'border-box',
          '&.visible': {
            visibility: 'visible',
            opacity: 1,
          },
        };

      case 'tabs':
        return {
          display: 'flex',
          boxShadow: 'none',
          flexDirection: 'column',
          gap: '8px',
          padding: '8px',
          backgroundColor: '#F7FAFC',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#F7FAFC',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#CBD5E0',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#A0AEC0',
          },

          width: '100%',
          height: '200px',
          borderRight: 'none',

          [theme.breakpoints.up('md')]: {
            width: '300px',
            height: '100%',
            borderBottom: 'none',
          },
        };

      case 'chatsHeader':
        return {
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.1)',
          gap: theme.spacing(1),
          padding: theme.spacing(1),
          backgroundColor: 'white',
          [theme.breakpoints.up('md')]: {
            padding: theme.spacing(2),
          },
        };

      case 'chatsInner':
        return {
          display: 'flex',
          boxShadow: 'none',
          backgroundColor: 'transparent',
          flexDirection: 'column',
          gap: theme.spacing(1),
          maxHeight: 'calc(100% - 48px)',

          [theme.breakpoints.up('md')]: {
            maxHeight: 'calc(100% - 60px)',
          },
        };

      case 'chatsItem':
        return {
          display: 'flex',
          padding: theme.spacing(1),
          backgroundColor: 'white',
          borderLeft: isActive ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
          boxShadow: isActive ? '0px 2px 8px rgba(0, 0, 0, 0.12)' : '0px 0px 4px 0px rgba(0, 0, 0, 0.1)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',

          [theme.breakpoints.up('md')]: {
            padding: theme.spacing(1.5),
          },
          '&:hover': {
            backgroundColor: isActive ? '#F7FAFC' : '#FAFAFA',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
          },
        };

      case 'chatsItemInner':
        return {
          display: 'flex',
          backgroundColor: 'transparent',
          alignItems: 'center',
          boxShadow: 'none',
          justifyContent: 'space-between',
          width: '100%',
        };

      case 'tabContent':
        return {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          height: 'calc(100% - 200px)',

          [theme.breakpoints.up('md')]: {
            height: '100%',
          },
        };

      case 'tabContentHeader':
        return {
          display: 'flex',
          alignItems: 'center',
          boxShadow: 'none',
          gap: theme.spacing(1),
          padding: theme.spacing(1),
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: 'white',

          [theme.breakpoints.up('md')]: {
            padding: theme.spacing(2),
          },
        };

      case 'messages':
        return {
          flex: 1,
          overflowY: 'auto',
          marginBottom: '16px',
          padding: '8px',
          display: 'flex',
          boxShadow: 'none',
          flexDirection: 'column',
          gap: '16px',
          [theme.breakpoints.up('md')]: {
            padding: theme.spacing(2),
          },
        };

      case 'messageBubble':
        return {
          display: 'flex',
          boxShadow: 'none',
          flexDirection: 'column',
          alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
          marginBottom: '12px',
          maxWidth: '85%',
          [theme.breakpoints.up('md')]: {
            maxWidth: '100%',
            marginBottom: theme.spacing(1.5),
          },
        };

      case 'messageContent':
        return {
          padding: '16px',
          maxWidth: '85%',
          wordWrap: 'break-word',
          backgroundColor: isCurrentUser ? '#5A7FF8' : '#F7FAFC',
          color: isCurrentUser ? '#ffffff' : '#2D3748',
          borderRadius: '16px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          transition: 'transform 0.2s ease-in-out',
          '&:hover': {
            transform: 'none',
          },
        };

      case 'input':
        return {
          display: 'flex',
          gap: '8px',
          padding: '16px',
          backgroundColor: 'white',
          borderTop: '1px solid #e0e0e0',
          alignItems: 'flex-end',
          [theme.breakpoints.up('md')]: {
            padding: theme.spacing(2),
            gap: theme.spacing(1.5),
          },
        };

      case 'inputButton':
        return {
          width: 40,
          boxShadow: 'none',
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',

          [theme.breakpoints.up('md')]: {
            width: 44,
            height: 44,
          },
        };

      case 'chatWidget':
        return {
          position: 'fixed',
          bottom: 24,
          right: 24,
          backgroundColor: 'transparent',
          boxShadow: 'none',
          zIndex: 1100,
          opacity: showChat && !openModal ? 1 : 0,
          transform: showChat && !openModal ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: showChat && !openModal ? 'auto' : 'none',
          visibility: 'visible',
        };

      case 'emptyChatState':
        return {
          display: 'flex',
          boxShadow: 'none',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: theme.palette.text.secondary,
          gap: theme.spacing(0.5),
          borderRadius: theme.shape.borderRadius * 2,

          padding: theme.spacing(2),
          marginLeft: theme.spacing(1),
          marginRight: theme.spacing(1),
          marginTop: theme.spacing(1),
          marginBottom: theme.spacing(1),

          [theme.breakpoints.up('md')]: {
            padding: theme.spacing(4),
            marginLeft: theme.spacing(2),
            marginRight: theme.spacing(2),
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
          },
        };

      case 'emptyChatStateIcon':
        return {
          display: 'flex',
          boxShadow: 'none',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          backgroundColor: 'rgba(90, 127, 248, 0.1)',
          color: theme.palette.primary.main,
          marginBottom: theme.spacing(1),

          width: 64,
          height: 64,

          [theme.breakpoints.up('md')]: {
            width: 80,
            height: 80,
          },
        };

      case 'chatModalHeader':
        return {
          display: 'flex',
          justifyContent: 'space-between',
          boxShadow: 'none',
          alignItems: 'center',
          padding: theme.spacing(2),
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: 'white',
        };

      case 'chatModalHeaderIcon':
        return {
          display: 'flex',
          boxShadow: 'none',
          gap: theme.spacing(1),
        };
    }
  },
);